import UniData from '@/common/classes/UniData'
import { IKey } from '@/common/constants/ikey'
import { extractUniDataTableRows } from '@/common/utils/extractUniDataTableRows'
import type { ListboxCatalogRow } from '@/common/utils/parseListboxCatalogFromColumnScript'
import { getFieldValueFromRow, type TreeTableColumnMeta } from '@/common/utils/treeTableRowKeys'

export type TreeTableGetDataColumn = TreeTableColumnMeta & {
  type?: string
  format?: unknown
  description?: string
  elementId?: string
  columnElementId?: string
  listboxCatalog?: ListboxCatalogRow[]
  defVisible?: boolean
  condition?: string
  width?: string
  swingOrderIndex?: number
  isMain?: boolean
  likeValueParentId?: boolean
  parentColumn?: string
}

export type TreeTableGetDataOptions = {
  /** Swing getData(null) / getFullData — все узлы backing-дерева. */
  addAll?: boolean
  /** Элемент expandByDefault=true → для узлов с детьми, как Swing getExpandsSelectedPaths(). */
  defaultExpandByDefault?: boolean
}

export type TreeTableGetDataRow = {
  fields: Record<string, { value: unknown; type: string }>
  errors: Record<string, never>
  attributes: Record<string, never>
}

const EXPORT_ITEMS_FIELD = IKey.ITEMS
const EXPORT_DROP_TARGET_FIELD = IKey.TT_IS_DROP_TARGET

const RESERVED_ROW_EXPORT_FIELDS = new Set([
  EXPORT_ITEMS_FIELD,
  EXPORT_DROP_TARGET_FIELD,
  IKey.TT_IS_ALLOW_CHILDREN,
  IKey.EXPAND_BY_DEFAULT,
  IKey.TT_FORBID_TO_SELECT_NODE,
  IKey.TT_HIDE_CHECKBOX,
  IKey.TT_COUNT_CHILDREN,
])

function sortColumnsForExport(columns: TreeTableGetDataColumn[]): TreeTableGetDataColumn[] {
  return [...columns].sort((a, b) => {
    const orderDiff =
      (a.order ?? Number.MAX_VALUE) - (b.order ?? Number.MAX_VALUE)
    if (orderDiff !== 0) return orderDiff
    return (a.swingOrderIndex ?? 0) - (b.swingOrderIndex ?? 0)
  })
}

const GENERIC_RUNTIME_COLUMN_TYPES = new Set(['TEXT', 'STRING', 'BOOLEAN', 'LONG', 'DOUBLE'])

const SEMANTIC_COLUMN_TYPES = new Set([
  'INLINEACTION',
  'LISTBOX',
  'LIST_BOX',
  'COMBOBOX',
  'COMBO',
  'CHECKBOX',
  'HTML',
  'MULTILINETEXT',
  'DATETIME',
  'DATE',
])

function normalizeColumnTypeToken(type: unknown): string {
  return String(type ?? '').trim().toUpperCase()
}

function resolveMergedColumnType(cfgType?: string, rtType?: string): string | undefined {
  const cfgNorm = normalizeColumnTypeToken(cfgType)
  const rtNorm = normalizeColumnTypeToken(rtType)
  if (SEMANTIC_COLUMN_TYPES.has(cfgNorm)) return cfgType
  if (rtType && !GENERIC_RUNTIME_COLUMN_TYPES.has(rtNorm)) return rtType
  return cfgType || rtType
}

function resolveMergedColumnFormat(
  cfgFormat: unknown,
  rtFormat: unknown,
  mergedType?: string,
): unknown {
  if (normalizeColumnTypeToken(mergedType) === 'INLINEACTION' && cfgFormat != null && cfgFormat !== '') {
    return cfgFormat
  }
  return rtFormat ?? cfgFormat
}

/** XML parent/unique flags не затираются runtime `{name}__columns` без явных значений. */
function mergeTreeTableContractColumnMeta(
  cfg: TreeTableGetDataColumn,
  rt: TreeTableGetDataColumn,
  swingOrderIndex: number,
): TreeTableGetDataColumn {
  const mergedType = resolveMergedColumnType(cfg.type, rt.type)
  return {
    ...cfg,
    ...rt,
    type: mergedType,
    format: resolveMergedColumnFormat(cfg.format, rt.format, mergedType),
    elementId: rt.elementId ?? cfg.elementId,
    defVisible: rt.defVisible ?? cfg.defVisible,
    width: rt.width ?? cfg.width,
    description: rt.description ?? cfg.description,
    swingOrderIndex,
    likeValueParentId: cfg.likeValueParentId === true || rt.likeValueParentId === true,
    parentColumn:
      rt.parentColumn != null && String(rt.parentColumn).trim() !== ''
        ? rt.parentColumn
        : cfg.parentColumn,
    uniqueValues: cfg.uniqueValues === true || rt.uniqueValues === true,
    uniqueNodeValues: cfg.uniqueNodeValues === true || rt.uniqueNodeValues === true,
    listboxCatalog: cfg.listboxCatalog ?? rt.listboxCatalog,
    columnElementId: rt.columnElementId ?? cfg.columnElementId ?? rt.elementId ?? cfg.elementId,
    condition: cfg.condition,
  }
}

export function mergeTreeTableExportColumns(
  configColumns: TreeTableGetDataColumn[],
  runtimeColumns: TreeTableGetDataColumn[] = [],
): TreeTableGetDataColumn[] {
  if (!configColumns.length) return sortColumnsForExport(runtimeColumns)
  if (!runtimeColumns.length) return sortColumnsForExport(configColumns)

  const runtimeByName = new Map(
    runtimeColumns.filter((c) => c.name).map((c) => [c.name as string, c]),
  )
  const merged: TreeTableGetDataColumn[] = []
  configColumns.forEach((cfg, index) => {
    if (!cfg.name) return
    const rt = runtimeByName.get(cfg.name)
    const swingOrderIndex = cfg.swingOrderIndex ?? index
    merged.push(
      rt ? mergeTreeTableContractColumnMeta(cfg, rt, swingOrderIndex) : { ...cfg, swingOrderIndex },
    )
  })
  for (const rt of runtimeColumns) {
    if (
      rt.name &&
      !RESERVED_ROW_EXPORT_FIELDS.has(rt.name) &&
      !merged.some((c) => c.name === rt.name)
    ) {
      merged.push({ ...rt, swingOrderIndex: merged.length + 1000 })
    }
  }
  return sortColumnsForExport(merged)
}

/** Порядок колонок как SwingTreeTable.getColNames (без служебных полей узла). */
export function buildTreeTableExportColumnNames(columns: TreeTableGetDataColumn[]): string[] {
  const colNames = sortColumnsForExport(columns)
    .map((c) => c.name)
    .filter((name): name is string => !!name && !RESERVED_ROW_EXPORT_FIELDS.has(name))
  return [IKey.ROW_CHECKED, 'value', ...colNames, EXPORT_ITEMS_FIELD, EXPORT_DROP_TARGET_FIELD]
}

function getExportedChildRows(row: TreeTableGetDataRow): TreeTableGetDataRow[] {
  const itemsVal = row.fields[EXPORT_ITEMS_FIELD]?.value
  const extracted = extractUniDataTableRows(itemsVal)
  if (extracted.length > 0) {
    return extracted as TreeTableGetDataRow[]
  }
  const rows = (itemsVal as { rows?: TreeTableGetDataRow[] } | null)?.rows
  return Array.isArray(rows) ? rows : []
}

/** Swing getDataChildren: флаги только у узлов с непустым items (после сборки дерева). */
function applySwingBranchFlagsPostProcess(
  rows: TreeTableGetDataRow[],
  defaultExpandByDefault?: boolean,
): void {
  for (const row of rows) {
    const children = getExportedChildRows(row)
    if (children.length > 0) {
      row.fields[IKey.TT_IS_ALLOW_CHILDREN] = { value: true, type: 'Boolean' }
      row.fields[IKey.EXPAND_BY_DEFAULT] = {
        value: !!defaultExpandByDefault,
        type: 'Boolean',
      }
      applySwingBranchFlagsPostProcess(children, defaultExpandByDefault)
    } else {
      delete row.fields[IKey.TT_IS_ALLOW_CHILDREN]
      delete row.fields[IKey.EXPAND_BY_DEFAULT]
    }
  }
}

function normalizeClientFieldType(valueType: string | undefined, value: unknown): string {
  const normalized = String(valueType || '').trim()
  if (normalized === 'Checkbox') return 'Boolean'
  if (
    normalized &&
    normalized !== 'Text' &&
    normalized !== 'Identificator'
  ) {
    return normalized
  }
  if (
    value != null &&
    typeof value === 'object' &&
    (Array.isArray((value as { rows?: unknown[] }).rows) ||
      Array.isArray((value as { value?: { rows?: unknown[] } }).value?.rows))
  ) {
    return 'UniDataTable'
  }
  if (typeof value === 'boolean') return 'Boolean'
  if (typeof value === 'number') return Number.isInteger(value) ? 'Long' : 'Double'
  return 'String'
}

function formatExportCellValue(value: unknown, columnType?: string): unknown {
  if (value === null || value === undefined || value === 'undefined') {
    if (columnType === 'Boolean' || columnType === 'Checkbox') return '0'
    return ''
  }
  if (typeof value === 'boolean') {
    return value ? '1' : '0'
  }
  if (columnType === 'Checkbox' || columnType === 'Boolean') {
    if (value === true || value === 'true' || value === 1 || value === '1') return '1'
    return '0'
  }
  if (
    value != null &&
    typeof value === 'object' &&
    (Array.isArray((value as { rows?: unknown[] }).rows) ||
      Array.isArray((value as { value?: { rows?: unknown[] } }).value?.rows))
  ) {
    return { rows: [], attributes: {}, columns: [], empty: true }
  }
  return value
}

function isTruthyFlag(value: unknown): boolean {
  return value === true || value === '1' || value === 1
}

/** Как Swing getDataChildren: isAllowChildren / expandByDefault только у узлов с детьми. */
function rowHasExportedChildren(nestedRows: unknown[]): boolean {
  return nestedRows.length > 0
}

function getNestedBackingRows(row: unknown): unknown[] {
  return extractUniDataTableRows(getFieldValueFromRow(row, EXPORT_ITEMS_FIELD))
}

function cloneRowWithItemsField(row: unknown, nestedRows: unknown[]): unknown {
  if (!row || typeof row !== 'object') return row
  const src = row as { fields?: Record<string, unknown>; errors?: unknown; attributes?: unknown }
  const fields = { ...(src.fields ?? {}) }
  fields[EXPORT_ITEMS_FIELD] = {
    value: { rows: nestedRows },
    type: 'UniDataTable',
  }
  return { ...src, fields }
}

/**
 * Если сервер отдал плоский список (parentId без вложенного items), собираем дерево для getData.
 */
export function nestFlatTreeTableRowsForExport(flatRows: unknown[]): unknown[] {
  if (!Array.isArray(flatRows) || flatRows.length === 0) return []

  const hasEmbeddedChildren = flatRows.some((row) => {
    if (getFieldValueFromRow(row, EXPORT_ITEMS_FIELD) === undefined) return false
    return getNestedBackingRows(row).length > 0
  })
  if (hasEmbeddedChildren) return flatRows

  const roots: unknown[] = []
  const childrenByParentId = new Map<string, unknown[]>()

  for (const row of flatRows) {
    const parentId = String(getFieldValueFromRow(row, 'parentId') ?? '').trim()
    if (!parentId) {
      roots.push(row)
      continue
    }
    const bucket = childrenByParentId.get(parentId) ?? []
    bucket.push(row)
    childrenByParentId.set(parentId, bucket)
  }

  const attach = (row: unknown): unknown => {
    const id = String(getFieldValueFromRow(row, '_id') ?? '').trim()
    const children = id ? childrenByParentId.get(id) ?? [] : []
    if (children.length === 0) return row
    const nested = children.map(attach)
    return cloneRowWithItemsField(row, nested)
  }

  if (roots.length === 0) return flatRows

  return roots.map(attach)
}

function fieldEntry(
  name: string,
  value: unknown,
  columnType?: string,
): [string, { value: unknown; type: string }] {
  return [name, { value, type: normalizeClientFieldType(columnType, value) }]
}

/** Порядок полей как в toString Swing: row__checked, value, колонки, items, _isDropTarget, isAllowChildren, expandByDefault. */
function buildExportRowFields(
  row: unknown,
  columns: TreeTableGetDataColumn[],
  options?: TreeTableGetDataOptions,
): Record<string, { value: unknown; type: string }> {
  const sortedColumns = sortColumnsForExport(columns)
  const colNames = buildTreeTableExportColumnNames(columns)
  const ordered: [string, { value: unknown; type: string }][] = []

  const rowCheckedRaw = getFieldValueFromRow(row, IKey.ROW_CHECKED)
  const rowChecked =
    rowCheckedRaw === undefined || rowCheckedRaw === null || rowCheckedRaw === ''
      ? '0'
      : String(rowCheckedRaw)
  ordered.push(fieldEntry(IKey.ROW_CHECKED, rowChecked))
  ordered.push(fieldEntry('value', ''))

  for (const col of sortedColumns) {
    const name = col.name
    if (!name || RESERVED_ROW_EXPORT_FIELDS.has(name)) continue
    const raw = getFieldValueFromRow(row, name)
    const plain = formatExportCellValue(raw, col.type)
    ordered.push(fieldEntry(name, plain, col.type))
  }

  const nestedRows = getNestedBackingRows(row)
  const hasChildren = rowHasExportedChildren(nestedRows)

  if (hasChildren) {
    const childExportRows = buildTreeTableGetDataRows(nestedRows, columns, options)
    ordered.push(
      fieldEntry(EXPORT_ITEMS_FIELD, {
        rows: childExportRows,
        attributes: {},
        columns: colNames,
        empty: childExportRows.length === 0,
      }),
    )
  }

  const dropTarget = getFieldValueFromRow(row, EXPORT_DROP_TARGET_FIELD)
  if (dropTarget !== undefined && dropTarget !== null && dropTarget !== '') {
    ordered.push(fieldEntry(EXPORT_DROP_TARGET_FIELD, dropTarget))
  }

  if (hasChildren) {
    // Swing getDataChildren: node.getAllowsChildren() / getExpandsSelectedPaths() для узлов с детьми
    ordered.push(fieldEntry(IKey.TT_IS_ALLOW_CHILDREN, true, 'Boolean'))

    const expandRaw = getFieldValueFromRow(row, IKey.EXPAND_BY_DEFAULT)
    const expandVal =
      options?.defaultExpandByDefault === true ||
      (options?.defaultExpandByDefault !== false && isTruthyFlag(expandRaw))
    ordered.push(fieldEntry(IKey.EXPAND_BY_DEFAULT, expandVal, 'Boolean'))
  }

  const fields: Record<string, { value: unknown; type: string }> = {}
  for (const [key, entry] of ordered) {
    fields[key] = entry
  }
  return fields
}

/** Строки TreeTable для getData (только колонки метаданных + служебные поля). */
export function buildTreeTableGetDataRows(
  backingRows: unknown[],
  columns: TreeTableGetDataColumn[],
  options?: TreeTableGetDataOptions,
): TreeTableGetDataRow[] {
  if (!Array.isArray(backingRows) || backingRows.length === 0) return []
  const rowsForExport = nestFlatTreeTableRowsForExport(backingRows)
  const exported = rowsForExport.map((row) => ({
    fields: buildExportRowFields(row, columns, options),
    errors: {},
    attributes: {},
  }))
  applySwingBranchFlagsPostProcess(exported, options?.defaultExpandByDefault)
  return exported
}

export function isTreeTableElementType(elementType: unknown): boolean {
  return String(elementType ?? '').trim().toUpperCase() === 'TREETABLE'
}

/** expandByDefault из XML элемента (текст или _attributes), как Swing getExpandsSelectedPaths(). */
export function resolveTreeTableExpandByDefault(
  expandByDefaultNode?: unknown,
  element?: { _attributes?: { expandByDefault?: unknown } } | null,
): boolean {
  if (expandByDefaultNode != null && expandByDefaultNode !== '') {
    if (typeof expandByDefaultNode === 'boolean') return expandByDefaultNode
    if (typeof expandByDefaultNode === 'object') {
      const obj = expandByDefaultNode as {
        _text?: unknown
        _attributes?: { expandByDefault?: unknown }
      }
      const inner = obj._text ?? obj._attributes?.expandByDefault
      if (inner != null && inner !== '') {
        const s = String(inner).trim().toLowerCase()
        if (s === 'true' || s === '1') return true
        if (s === 'false' || s === '0') return false
      }
    } else {
      const s = String(expandByDefaultNode).trim().toLowerCase()
      if (s === 'true' || s === '1') return true
      if (s === 'false' || s === '0') return false
    }
  }
  const attr = element?._attributes?.expandByDefault
  if (attr != null && attr !== '') {
    const s = String(attr).trim().toLowerCase()
    return s === 'true' || s === '1'
  }
  return false
}

/** Runtime-колонки из pageFields (`fieldName__columns`). */
export function parseTreeTableRuntimeColumnsFromPage(
  pageFields: Record<string, unknown> | null | undefined,
  fieldName: string,
): TreeTableGetDataColumn[] {
  if (!pageFields || !fieldName) return []
  const rawColumns =
    (pageFields[`${fieldName}__columns`] as { value?: { rows?: unknown[] } } | undefined)?.value
      ?.rows ??
    (pageFields[`${fieldName}__columns`] as { rows?: unknown[] } | undefined)?.rows
  if (!Array.isArray(rawColumns)) return []

  const columns: TreeTableGetDataColumn[] = []
  rawColumns.forEach((column: unknown, index: number) => {
    const fields = (column as { fields?: Record<string, { value?: unknown }> })?.fields ?? {}
    const name = fields.name?.value
    if (name == null || name === '') return
    const isMainRaw = fields.isMain?.value
    columns.push({
      name: String(name),
      order: fields.order?.value != null ? Number(fields.order.value) : undefined,
      type: fields.type?.value != null ? String(fields.type.value) : undefined,
      isMain: isMainRaw === true || isMainRaw === '1' || isMainRaw === 'true',
      swingOrderIndex: index,
    })
  })
  return columns
}

/** Поле таблицы для WS getData (пересборка Swing getDataChildren). */
export function rebuildTreeTableGetDataForWs(
  fieldName: string,
  backingRows: unknown[],
  columns: TreeTableGetDataColumn[],
  options?: TreeTableGetDataOptions,
): unknown {
  const ud = buildTreeTableUniData(fieldName, backingRows, columns, options)
  return extractUniDataFieldFromHookResult(ud, fieldName)
}

/** UniData с одним полем таблицы — как SwingTreeTable.getData(null). */
export function buildTreeTableUniData(
  fieldName: string,
  backingRows: unknown[],
  columns: TreeTableGetDataColumn[],
  options?: TreeTableGetDataOptions,
): UniData {
  const colNames = buildTreeTableExportColumnNames(columns)
  const rows = buildTreeTableGetDataRows(backingRows, columns, options)
  const ud = new UniData()
  ud.setField(fieldName, { rows, columns: colNames })
  return ud
}

/** Извлечь поле таблицы из результата element.getData() (UniData или plain). */
export function extractUniDataFieldFromHookResult(
  hookData: unknown,
  fieldName: string,
): unknown {
  if (hookData == null || typeof hookData !== 'object') return undefined
  const hook = hookData as {
    getField?: (name: string) => { value?: unknown }
    _getAllFields?: () => Record<string, unknown>
  }

  if (typeof hook.getField === 'function') {
    const tf = hook.getField(fieldName)
    if (tf && typeof tf === 'object' && 'value' in tf) {
      return tf
    }
  }

  if (typeof hook._getAllFields === 'function') {
    const all = hook._getAllFields()
    if (all && typeof all === 'object' && fieldName in all) {
      return all[fieldName]
    }
  }

  if (fieldName in (hookData as Record<string, unknown>)) {
    return (hookData as Record<string, unknown>)[fieldName]
  }

  return undefined
}
